import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  detectRegionFromTimezone,
  isSupportedTimezone,
  REGIONS,
  type RegionCode,
  type RegionConfig,
} from '../../config/regions';

export interface UseRegionReturn {
  regionCode: RegionCode | null;
  regionConfig: RegionConfig | null;
  isSupported: boolean;
  regionName: string | null;
  timezone: string | null;
}

/**
 * Custom hook for detecting and managing region-specific phone functionality
 *
 * This hook determines the user's region based on their organization's timezone
 * and provides region-specific configuration for phone validation and formatting.
 *
 * @returns {UseRegionReturn} Region information and configuration
 *
 * @example
 * ```typescript
 * const { regionCode, regionConfig, isSupported } = useRegion();
 *
 * if (!isSupported) {
 *   return <Alert>Phone not available in your region</Alert>;
 * }
 *
 * // Use regionConfig for validation
 * const isValid = regionConfig.phoneValidation.regex.test(phoneNumber);
 * ```
 */
export const useRegion = (): UseRegionReturn => {
  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );

  const timezone = selectedOrganization?.company?.timezone || null;

  const regionCode = useMemo(() => {
    if (!timezone) return null;
    return detectRegionFromTimezone(timezone);
  }, [timezone]);

  const regionConfig = regionCode ? REGIONS[regionCode] : null;
  const isSupported = isSupportedTimezone(timezone || '');
  const regionName = regionConfig?.name || null;

  return {
    regionCode,
    regionConfig,
    isSupported,
    regionName,
    timezone,
  };
};
